import { useEffect, useState } from 'react';
import { Card, Form, Button, Spinner, Alert, Badge, Row, Col } from 'react-bootstrap';
import type { components } from '@telemedicine/shared';
import { listRecommendations, createRecommendation } from '../services/api';
import { useToast } from '../components/ToastProvider';
import { BusyButton } from '../components/BusyButton';
import { errMessage } from '../errors';
import { getLang } from '../language';
import { personName } from '../people';
import { enumLabel } from '../labels';

type Case = components['schemas']['Case'];
type Recommendation = components['schemas']['Recommendation'];
type Outcome = components['schemas']['RecommendationOutcome'];

// Video consultation is its own action (the Video consultation card), not a recommendation
// outcome, so VIDEO_CONSULTATION_REQUIRED is intentionally omitted here (it drives no state).
// FACE_TO_FACE_SPECIALIST_REQUIRED is likewise omitted until that flow is built.
const OUTCOMES: Outcome[] = [
  'WRITTEN_ADVICE',
  'FURTHER_INFORMATION_REQUIRED',
  'NO_FURTHER_ACTION_REQUIRED',
  'OTHER',
];

/**
 * Recommendations for a case. Lists any existing ones, and — for the assigned reviewer while the
 * case is under review — a form to record one (outcome + narrative + follow-up). Recording a
 * recommendation advances the case status and appends a decision-log entry (server-side).
 */
export function CaseRecommendations({
  theCase,
  roles,
  onChanged,
  refreshKey,
  onCount,
}: {
  theCase: Case;
  roles: string[];
  onChanged: () => void;
  /** Bumped by the parent's background poll to re-fetch quietly. */
  refreshKey?: number;
  /** Report the current item count so the parent can badge this tab when it grows unseen. */
  onCount?: (n: number) => void;
}) {
  const [items, setItems] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const [outcome, setOutcome] = useState<Outcome>('WRITTEN_ADVICE');
  const [narrative, setNarrative] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [saving, setSaving] = useState(false);

  const isReviewer = roles.includes('REVIEWER') || roles.includes('ADMIN') || roles.includes('GLOBAL_ADMIN');
  // Reviewer can record a recommendation whenever the case is with them: while reviewing, or
  // after a video / face-to-face consultation (they can conclude directly without cancelling the
  // call first). In WAITING_FOR_LOCAL_DOCTOR the ball is with the local doctor, so the form is
  // hidden until they resume review (see CaseActions).
  const canRecord =
    isReviewer &&
    ['ASSIGNED', 'UNDER_REVIEW', 'VIDEO_REQUIRED', 'SPECIALIST_FACE_TO_FACE'].includes(
      theCase.status,
    );

  const refresh = () => {
    listRecommendations(theCase.caseId)
      .then((r) => {
        setItems(r.items);
        onCount?.(r.items.length);
      })
      .catch(() => setError('Failed to load recommendations'))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, [theCase.caseId, refreshKey]);

  const onSubmit = async () => {
    if (!narrative.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await createRecommendation(theCase.caseId, {
        version: theCase.version,
        outcome,
        recommendation: narrative.trim(),
        followUp: followUp.trim() || null,
        sourceLang: getLang(),
      });
      setNarrative('');
      setFollowUp('');
      toast.success('Recommendation recorded.');
      refresh();
      onChanged();
    } catch (e) {
      const msg = errMessage(e, 'Failed to record recommendation');
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="shadow-sm mb-3">
      <Card.Header>Recommendations</Card.Header>
      <Card.Body>
        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

        {loading ? (
          <div className="text-muted d-flex align-items-center">
            <Spinner animation="border" size="sm" className="me-2" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <p className="text-muted">No recommendations yet.</p>
        ) : (
          <div className="mb-3">
            {items.map((r) => (
              <div key={r.recommendationId} className="border rounded p-2 mb-2">
                <Badge bg="primary" className="mb-1">{enumLabel('recommendationOutcome', r.outcome, getLang())}</Badge>
                <div>{r.recommendation.original}</div>
                {r.recommendation.translated && (
                  <div className="text-muted fst-italic mt-1">
                    {r.recommendation.translated}{' '}
                    <Badge bg="info">machine translated</Badge>
                  </div>
                )}
                {r.followUp?.original && (
                  <div className="mt-2"><strong>Follow-up:</strong> {r.followUp.original}</div>
                )}
                <div className="small text-muted mt-1">
                  {personName(r.reviewerName, r.reviewerId)} · {new Date(r.createdAt).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}

        {canRecord && (
          <>
            <hr />
            <h3 className="h6">Record a recommendation</h3>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Outcome</Form.Label>
                  <Form.Select value={outcome} onChange={(e) => setOutcome(e.target.value as Outcome)}>
                    {OUTCOMES.map((o) => <option key={o} value={o}>{enumLabel('recommendationOutcome', o, getLang())}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Recommendation</Form.Label>
              <Form.Control as="textarea" rows={4} value={narrative} onChange={(e) => setNarrative(e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Follow-up (optional)</Form.Label>
              <Form.Control as="textarea" rows={2} value={followUp} onChange={(e) => setFollowUp(e.target.value)} />
            </Form.Group>
            <BusyButton variant="success" onClick={onSubmit} loading={saving} busyLabel="Saving…" disabled={!narrative.trim()}>
              Record recommendation
            </BusyButton>
          </>
        )}
      </Card.Body>
    </Card>
  );
}
